import { action, makeObservable, observable, runInAction } from 'mobx';
import { StateType } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import {
  TwoFactorAuthMethods,
  TwoFactorAuthPhoneDeliveryMethod,
} from '../utils/constants';
import {
  ERROR_CODES,
  extractServerErrorCode,
  extractErrorKeyByErrorCode,
} from '../utils/errorHandler';
import { AuthenticateByCodeStore } from './authenticateByCode';
import { RootStore } from './root';
import { RequireRegistrationFactorData } from './signupEmailFactorTypes';
import { ROUTES } from '../routes';
import { SIGNUP_EMAIL_STORAGE_KEY } from './constants';

const RESEND_COOLDOWN_MS = 30_000;

// Expiry can arrive in errorData instead of errorCode, as in login 2FA.
const isExpiredChallenge = (error: unknown) =>
  (error as any)?.errorData === ERROR_CODES.TOKEN_EXPIRED_IAM ||
  [ERROR_CODES.TOKEN_EXPIRED_IAM, ERROR_CODES.EXPIRED_JWT_TOKEN].includes(
    extractServerErrorCode(error),
  );

export class SignupEmailFactorStore extends AuthenticateByCodeStore {
  readonly allowRememberMe = false;
  email = '';
  isLoading = false;
  hasSentCode = false;
  // When the Resend link unlocks again; every sent code restarts the cooldown.
  resendAvailableAt = 0;
  // Set once the code is verified; the route stays mounted showing a loader
  // until postSignup leaves the page, instead of falling back to the email step.
  isCompleting = false;
  private requestVersion = 0;

  constructor(rootStore: RootStore) {
    super(rootStore);
    this.SUBMIT_INTERACTION_NAME = 'signup-verify-email-code';
    this.currentTwoFAMethod = TwoFactorAuthMethods.Email;
    makeObservable<this, 'reset' | 'completeRegistration'>(this, {
      email: observable,
      stateToken: observable,
      twoFAHint: observable,
      isLoading: observable,
      hasSentCode: observable,
      resendAvailableAt: observable,
      isCompleting: observable,
      setAuthParams: action,
      handleError: action,
      reset: action,
      completeRegistration: action,
      start: action.bound,
      verify: action.bound,
      resendCode: action.bound,
      backToEmail: action.bound,
      switchToPassword: action.bound,
    });
    if (rootStore.navigationStore.currentRoute === ROUTES.SIGNUP_EMAIL_CODE) {
      this.email = rootStore.userDataStore.email;
      this.backToEmail();
    }
  }

  setAuthParams(payload: {
    stateToken?: string | null;
    requireRegistrationFactorData?: RequireRegistrationFactorData | null;
  }) {
    this.stateToken = payload.stateToken ?? undefined;
    this.twoFAHint = this.email;
    this.hasSentCode = Boolean(this.stateToken);
    if (this.hasSentCode) {
      this.resendAvailableAt = Date.now() + RESEND_COOLDOWN_MS;
    }
    this.clear();
  }

  enableAccess() {
    return (
      Boolean(this.email) &&
      this.rootStore.signupStore.isSignupWithEmailCodeEnabled
    );
  }

  async start(email: string) {
    if (this.isLoading) {
      return;
    }
    if (this.email !== email) {
      this.reset();
      this.email = email;
      this.twoFAHint = email;
    }
    try {
      sessionStorage.setItem(SIGNUP_EMAIL_STORAGE_KEY, email);
    } catch (_) {}
    if (!this.stateToken && !this.hasSentCode) {
      const sendError = await this.resendCode({ lockOnFailure: false });
      if (sendError) {
        // Nothing was sent, so do not open a screen that claims otherwise.
        return sendError;
      }
      if (this.isCompleting) {
        return;
      }
    }
    if (this.email === email) {
      runInAction(() => {
        this.rootStore.signupStore.isEmailSignupMode = false;
      });
      this.rootStore.navigationStore.navigate(ROUTES.SIGNUP_EMAIL_CODE);
    }
  }

  /**
   * Sends a code (first send and the Resend link); each sent code restarts the
   * cooldown. A failed resend also restarts it and shows the error on the code
   * field, so the link cannot be hammered; a failed first send leaves it open.
   */
  async resendCode({
    lockOnFailure = true,
  }: {
    phoneDeliveryMethod?: TwoFactorAuthPhoneDeliveryMethod;
    lockOnFailure?: boolean;
  } = {}) {
    if (this.isLoading || !this.enableAccess()) {
      return ERROR_CODES.GENERAL_ERROR_CODE;
    }
    this.isLoading = true;
    const version = this.requestVersion;
    let sendError: string | undefined;
    this.rootStore.panorama.startTransaction('signup-send-email-code');
    try {
      if (this.stateToken) {
        const resent = await this.resendOnOpenChallenge(this.stateToken);
        if (version !== this.requestVersion) {
          return undefined;
        }
        if (resent) {
          this.setAuthParams({ stateToken: resent.stateToken });
          return undefined;
        }
      }
      // A new deferred registration issues a fresh challenge without creating an account.
      const payload = await this.rootStore.signupStore.requestEmailCode(
        this.email,
      );
      if (version !== this.requestVersion) {
        return undefined;
      }
      if (payload.registered) {
        // The backend did not pause on a factor: the account exists and the
        // session is set, so finish exactly as a verified code would.
        this.completeRegistration(payload.payload);
        return undefined;
      }
      this.setAuthParams(payload);
      return undefined;
    } catch (error) {
      const errorCode =
        extractServerErrorCode(error) || ERROR_CODES.GENERAL_ERROR_CODE;
      if (version === this.requestVersion) {
        this.rootStore.modalModeHandlerStore.handleErrorReport(errorCode);
      }
      sendError = errorCode;
    } finally {
      this.rootStore.panorama.finishTransaction('signup-send-email-code');
      if (version === this.requestVersion) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
    if (!sendError || version !== this.requestVersion) {
      return sendError;
    }
    if (this.rootStore.captchaStore.isCaptchaServerError(sendError)) {
      // The OTP screen has no visible CAPTCHA; the password form renders one
      // once it mounts. Drop the earlier challenge so switching back to the
      // code sends a new one with the solved CAPTCHA instead of reusing it.
      runInAction(() => {
        this.stateToken = undefined;
        this.hasSentCode = false;
      });
      this.switchToPassword();
      this.rootStore.signupStore.showCaptchaAfterBlockedSend(sendError);
    } else if (lockOnFailure) {
      runInAction(() => {
        this.resendAvailableAt = Date.now() + RESEND_COOLDOWN_MS;
        this.showCodeFieldError(sendError);
      });
    }
    return sendError;
  }

  /**
   * Asks the open challenge for a new code. Returns undefined when the
   * challenge expired, so the caller starts a new registration instead.
   */
  private async resendOnOpenChallenge(stateToken: string) {
    try {
      const response =
        await this.rootStore.apiStore.resendSignupEmailCode(stateToken);
      return { stateToken: response.stateToken || stateToken };
    } catch (error) {
      if (isExpiredChallenge(error)) {
        return undefined;
      }
      throw error;
    }
  }

  handleResendSendFailure() {
    return true;
  }

  async verify({ code }: { code: string }) {
    if (this.isLoading) {
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      // Sign Up stays enabled, so an incomplete code shows the field's error.
      this.codeField.triggerValidator();
      this.codeField.markFieldAsDirty();
      return;
    }
    if (!this.stateToken) {
      // A sent code whose challenge expired: say so again instead of ignoring
      // the click. With nothing sent yet, Resend is the only way forward.
      if (this.hasSentCode) {
        this.handleError(ERROR_CODES.TOKEN_EXPIRED_IAM);
      }
      return;
    }
    this.isLoading = true;
    const version = this.requestVersion;
    this.rootStore.panorama.startTransaction(this.SUBMIT_INTERACTION_NAME);
    try {
      const data = await this.rootStore.apiStore.verifyCodeAndLoginWithIAM({
        stateToken: this.stateToken,
        twoFAMethod: TwoFactorAuthMethods.Email,
        twoFAToken: '',
        code,
      });
      if (version !== this.requestVersion) {
        return;
      }
      const payload = data?.data;
      if (!data.success || payload?.state !== StateType.SUCCESS) {
        this.handleError(ERROR_CODES.GENERAL_ERROR_CODE);
        return;
      }
      this.completeRegistration(payload);
      return;
    } catch (error) {
      if (version !== this.requestVersion) {
        return;
      }
      const expired = isExpiredChallenge(error);
      const errorCode =
        (error as any)?.errorData === ERROR_CODES.TOKEN_EXPIRED_IAM
          ? ERROR_CODES.TOKEN_EXPIRED_IAM
          : extractServerErrorCode(error) || ERROR_CODES.GENERAL_ERROR_CODE;
      if (expired) {
        runInAction(() => {
          this.stateToken = undefined;
        });
      }
      this.handleError(errorCode);
    } finally {
      this.rootStore.panorama.finishTransaction(this.SUBMIT_INTERACTION_NAME);
      if (version === this.requestVersion && !this.isCompleting) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  }

  private completeRegistration(
    payload: Parameters<RootStore['navigationStore']['postSignup']>[0],
  ) {
    runInAction(() => {
      this.isCompleting = true;
    });
    try {
      sessionStorage.removeItem(SIGNUP_EMAIL_STORAGE_KEY);
    } catch (_) {}
    this.rootStore.navigationStore.postSignup(payload);
  }

  handleError(errorCode: string) {
    this.rootStore.modalModeHandlerStore.handleErrorReport(errorCode);
    this.showCodeFieldError(errorCode);
  }

  private showCodeFieldError(errorCode: string) {
    this.codeField.addError(
      this.rootStore.i18n.t(
        extractErrorKeyByErrorCode(errorCode, { context: 'email' }),
      ),
    );
    this.codeField.markFieldAsDirty();
  }

  switchToPassword() {
    if (this.isLoading) {
      return;
    }
    const { signupStore, userDataStore, navigationStore } = this.rootStore;
    userDataStore.setEmail(this.email);
    signupStore.onInitFields();
    signupStore.emailRepeatField.setValue(this.email);
    signupStore.prefersPassword = true;
    signupStore.isEmailSignupMode = true;
    navigationStore.navigate(ROUTES.SIGNUP_PASSWORD);
  }

  backToEmail() {
    const { signupStore, emailStepStore, userDataStore, navigationStore } =
      this.rootStore;
    const email =
      (navigationStore.currentRoute === ROUTES.SIGNUP_PASSWORD &&
        signupStore.emailField.value) ||
      this.email ||
      userDataStore.email;
    this.reset();
    userDataStore.setEmail(email);
    signupStore.onInitFields();
    signupStore.prefersPassword = false;
    signupStore.isEmailSignupMode = false;
    emailStepStore.emailField.setValue(email);
    emailStepStore.isFetchingByDefaultEmail = false;
    navigationStore.navigate(ROUTES.SIGNUP_PASSWORD);
  }

  private reset() {
    this.requestVersion++;
    this.email = '';
    this.stateToken = undefined;
    this.hasSentCode = false;
    this.resendAvailableAt = 0;
    this.isLoading = false;
    this.isCompleting = false;
    this.clear();
    try {
      sessionStorage.removeItem(SIGNUP_EMAIL_STORAGE_KEY);
    } catch (_) {}
  }
}
